import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

const API_BASE = import.meta.env.VITE_API_URL || 'https://pineapple-pos-backend-production.up.railway.app/api'
const LOCATION_ID = import.meta.env.VITE_LOCATION_ID || 'hanky-panky-buns'

export default function Order() {
  const navigate = useNavigate()

  // Menu state
  const [categories, setCategories] = useState([])
  const [menuItems, setMenuItems] = useState({})
  const [modifierGroups, setModifierGroups] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [itemViewStep, setItemViewStep] = useState('info')
  const [itemModifiers, setItemModifiers] = useState({})
  const [itemQuantity, setItemQuantity] = useState(1)
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [requiredModifierIndex, setRequiredModifierIndex] = useState(0)

  // Cart state
  const [cart, setCart] = useState([])

  // Checkout state
  const [checkoutStep, setCheckoutStep] = useState(null)
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [orderNumber, setOrderNumber] = useState(null)
  const [paymentError, setPaymentError] = useState(null)

  // Handle Stripe redirect on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')

    if (sessionId) {
      setCheckoutStep('processing')
      const savedName = localStorage.getItem('order_customer_name') || ''
      setCustomerName(savedName)

      fetch(`${API_BASE}/kiosk/verify-session?session_id=${sessionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setOrderNumber(data.orderNumber)
            setCart([])
            localStorage.removeItem('order_customer_name')
            localStorage.removeItem('order_customer_phone')
            navigate('/order/success', { state: { orderNumber: data.orderNumber, customerName: savedName } })
          } else {
            setPaymentError(data.error || 'Payment verification failed')
            setCheckoutStep('payment')
          }
        })
        .catch(err => {
          console.error('Verify session error:', err)
          setPaymentError('Failed to verify payment')
          setCheckoutStep('payment')
        })
    }
  }, [navigate])

  // Fetch menu
  useEffect(() => {
    fetchMenu()
  }, [])

  const fetchMenu = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/menu/full?locationId=${LOCATION_ID}`)
      const data = await response.json()

      setCategories(data.categories || [])
      setMenuItems(data.items || {})
      setModifierGroups(data.modifierGroups || {})

      if (data.categories && data.categories.length > 0) {
        setSelectedCategory(data.categories[0].id)
      }
    } catch (err) {
      console.error('Failed to fetch menu:', err)
      setError('Failed to load menu. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Get items for selected category
  const categoryItems = useMemo(() => {
    if (!selectedCategory || !menuItems) return []
    const category = categories.find(c => c.id === selectedCategory)
    if (!category) return []
    return menuItems[category.slug] || []
  }, [selectedCategory, menuItems, categories])

  // Calculate cart total
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.totalPrice, 0)
  }, [cart])

  // Get required modifier groups for an item
  const getRequiredGroups = (item) => {
    if (!item?.modifierGroups) return []
    return item.modifierGroups
      .map(slug => modifierGroups[slug])
      .filter(g => g && g.required)
      .sort((a, b) => (b.popupPriority || 0) - (a.popupPriority || 0))
  }

  // Handle item selection
  const handleSelectItem = (item) => {
    if (!item.available) return
    setSelectedItem(item)
    setItemModifiers({})
    setItemQuantity(1)
    setSpecialInstructions('')
    setItemViewStep('info')
    setRequiredModifierIndex(0)
  }

  // Proceed from info to modifiers or add to cart
  const handleProceedFromInfo = () => {
    const requiredGroups = getRequiredGroups(selectedItem)
    const optionalGroups = (selectedItem.modifierGroups || [])
      .map(slug => modifierGroups[slug])
      .filter(g => g && !g.required)

    if (requiredGroups.length > 0) {
      setItemViewStep('required')
      setRequiredModifierIndex(0)
    } else if (optionalGroups.length > 0) {
      setItemViewStep('optional')
    } else {
      handleAddToCart()
    }
  }

  // Handle modifier selection
  const handleModifierChange = (groupId, modifierId, isMultiSelect) => {
    setItemModifiers(prev => {
      const current = prev[groupId] || []
      if (isMultiSelect) {
        if (current.includes(modifierId)) {
          return { ...prev, [groupId]: current.filter(id => id !== modifierId) }
        } else {
          return { ...prev, [groupId]: [...current, modifierId] }
        }
      } else {
        return { ...prev, [groupId]: [modifierId] }
      }
    })
  }

  // Handle required modifier selection
  const handleRequiredModifierSelect = (groupId, modifierId, isMultiSelect) => {
    handleModifierChange(groupId, modifierId, isMultiSelect)
    if (!isMultiSelect) {
      advanceRequiredModifier()
    }
  }

  // Advance to next required modifier
  const advanceRequiredModifier = () => {
    const requiredGroups = getRequiredGroups(selectedItem)
    if (requiredModifierIndex < requiredGroups.length - 1) {
      setRequiredModifierIndex(requiredModifierIndex + 1)
    } else {
      const optionalGroups = (selectedItem.modifierGroups || [])
        .map(slug => modifierGroups[slug])
        .filter(g => g && !g.required)
      if (optionalGroups.length > 0) {
        setItemViewStep('optional')
      } else {
        handleAddToCart()
      }
    }
  }

  // Calculate item price with modifiers
  const calculateItemPrice = () => {
    if (!selectedItem) return 0
    let price = selectedItem.price

    Object.entries(itemModifiers).forEach(([groupId, modIds]) => {
      const group = modifierGroups[groupId]
      if (group && group.options) {
        modIds.forEach(modId => {
          const mod = group.options.find(o => o.id === modId)
          if (mod && mod.price) {
            price += mod.price
          }
        })
      }
    })

    return price * itemQuantity
  }

  // Add item to cart
  const handleAddToCart = () => {
    if (!selectedItem) return

    const selectedMods = []
    Object.entries(itemModifiers).forEach(([groupId, modIds]) => {
      const group = modifierGroups[groupId]
      if (group && group.options) {
        modIds.forEach(modId => {
          const mod = group.options.find(o => o.id === modId)
          if (mod) {
            selectedMods.push({
              id: mod.id,
              name: mod.name,
              price: mod.price || 0,
            })
          }
        })
      }
    })

    const cartItem = {
      id: Date.now(),
      menuItemId: selectedItem.id,
      name: selectedItem.name,
      price: selectedItem.price,
      quantity: itemQuantity,
      modifiers: selectedMods,
      specialInstructions,
      totalPrice: calculateItemPrice(),
    }

    setCart(prev => [...prev, cartItem])
    setSelectedItem(null)
  }

  // Remove item from cart
  const handleRemoveFromCart = (cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId))
  }

  // Start checkout
  const handleStartCheckout = () => {
    if (cart.length === 0) return
    setCheckoutStep('info')
  }

  // Format phone number
  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '')
    if (phone.length <= 3) return phone
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`
  }

  // Handle phone input
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value)
    setCustomerPhone(formatted)
  }

  // Proceed to payment
  const handleProceedToPayment = () => {
    if (!customerName.trim()) {
      setPaymentError('Please enter your name')
      return
    }
    setPaymentError(null)
    localStorage.setItem('order_customer_name', customerName)
    localStorage.setItem('order_customer_phone', customerPhone)
    setCheckoutStep('payment')
  }

  // Process payment
  const handlePayment = async () => {
    setCheckoutStep('processing')
    setPaymentError(null)

    try {
      const response = await fetch(`${API_BASE}/kiosk/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locationId: LOCATION_ID,
          customerPhone: customerPhone.replace(/\D/g, ''),
          customerName: customerName || 'Guest',
          items: cart.map(item => ({
            menuItemId: item.menuItemId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            modifiers: item.modifiers,
            specialInstructions: item.specialInstructions,
          })),
          total: cartTotal,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Payment failed')
      }

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
        return
      }

      setOrderNumber(result.orderNumber)
      setCart([])
      navigate('/order/success', { state: { orderNumber: result.orderNumber, customerName } })

    } catch (err) {
      console.error('Checkout error:', err)
      setPaymentError(err.message)
      setCheckoutStep('payment')
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading menu...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchMenu}
            className="px-6 py-2 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Checkout flow
  if (checkoutStep) {
    return (
      <div className="min-h-[60vh] py-8">
        <div className="max-w-md mx-auto px-4">
          {/* Back button */}
          <button
            onClick={() => setCheckoutStep(null)}
            className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Menu
          </button>

          <div className="bg-zinc-900 rounded-2xl p-6 border border-yellow-500/20">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-yellow-500">Checkout</h2>
              <span className="text-xl font-bold text-white">${cartTotal.toFixed(2)}</span>
            </div>

            {/* Customer Info Step */}
            {checkoutStep === 'info' && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Your Name</h3>
                <p className="text-gray-400 text-sm mb-4">We will call your name when your order is ready!</p>

                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-zinc-800 border border-yellow-500/30 rounded-xl text-white text-lg focus:outline-none focus:border-yellow-500 mb-4"
                  autoFocus
                />

                <label className="block text-sm text-gray-400 mb-2">Phone (optional)</label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 555-5555"
                  className="w-full px-4 py-3 bg-zinc-800 border border-yellow-500/30 rounded-xl text-white focus:outline-none focus:border-yellow-500 mb-4"
                />

                {paymentError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-4">
                    {paymentError}
                  </div>
                )}

                <button
                  onClick={handleProceedToPayment}
                  disabled={!customerName.trim()}
                  className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/50 text-black font-bold text-lg rounded-xl transition-colors"
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {/* Payment Step */}
            {checkoutStep === 'payment' && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>

                <div className="bg-zinc-800 rounded-xl p-4 mb-6">
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between py-2 border-b border-zinc-700 last:border-0">
                      <span className="text-gray-300">{item.quantity}x {item.name}</span>
                      <span className="text-white">${item.totalPrice.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 mt-2 border-t border-yellow-500/30">
                    <span className="font-bold text-yellow-500">Total</span>
                    <span className="font-bold text-white">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                {paymentError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-4">
                    {paymentError}
                  </div>
                )}

                <button
                  onClick={handlePayment}
                  className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg rounded-xl transition-colors"
                >
                  Pay ${cartTotal.toFixed(2)}
                </button>

                <p className="text-center text-gray-500 text-sm mt-4">
                  You will be redirected to secure payment
                </p>
              </div>
            )}

            {/* Processing Step */}
            {checkoutStep === 'processing' && (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Processing your order...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Item Info Modal
  if (selectedItem && itemViewStep === 'info') {
    return (
      <ItemInfoModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onContinue={handleProceedFromInfo}
        hasModifiers={
          getRequiredGroups(selectedItem).length > 0 ||
          (selectedItem.modifierGroups || [])
            .map(slug => modifierGroups[slug])
            .filter(g => g && !g.required).length > 0
        }
      />
    )
  }

  // Required Modifier Modal
  if (selectedItem && itemViewStep === 'required') {
    const requiredGroups = getRequiredGroups(selectedItem)
    const currentGroup = requiredGroups[requiredModifierIndex]

    if (currentGroup) {
      return (
        <ModifierModal
          group={currentGroup}
          selectedModifiers={itemModifiers[currentGroup.id] || []}
          onSelect={(modId) => handleRequiredModifierSelect(currentGroup.id, modId, currentGroup.multiSelect)}
          onContinue={advanceRequiredModifier}
          onClose={() => setSelectedItem(null)}
          progress={`${requiredModifierIndex + 1}/${requiredGroups.length}`}
        />
      )
    }
  }

  // Optional Modifiers Modal
  if (selectedItem && itemViewStep === 'optional') {
    const optionalGroups = (selectedItem.modifierGroups || [])
      .map(slug => modifierGroups[slug])
      .filter(g => g && !g.required)

    return (
      <OptionalModifiersModal
        item={selectedItem}
        groups={optionalGroups}
        selectedModifiers={itemModifiers}
        onModifierChange={handleModifierChange}
        specialInstructions={specialInstructions}
        onSpecialInstructionsChange={setSpecialInstructions}
        quantity={itemQuantity}
        onQuantityChange={setItemQuantity}
        totalPrice={calculateItemPrice()}
        onAddToCart={handleAddToCart}
        onClose={() => setSelectedItem(null)}
      />
    )
  }

  // Main order view
  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              Order <span className="text-yellow-500">Online</span>
            </h1>
            <p className="text-gray-400 mt-1">Select items to add to your order</p>
          </div>

          {cart.length > 0 && (
            <button
              onClick={handleStartCheckout}
              className="px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors flex items-center gap-2"
            >
              <span>Cart ({cart.length})</span>
              <span>${cartTotal.toFixed(2)}</span>
            </button>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Categories */}
          <div className="lg:w-48 flex-shrink-0">
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-4 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                    selectedCategory === cat.id
                      ? 'bg-yellow-500 text-black'
                      : 'bg-zinc-800 text-white hover:bg-zinc-700 border border-yellow-500/20'
                  }`}
                >
                  {cat.icon && <span className="text-xl">{cat.icon}</span>}
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex-1">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {categoryItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  disabled={!item.available}
                  className={`text-left bg-zinc-900 rounded-2xl overflow-hidden border border-yellow-500/20 hover:border-yellow-500/40 transition-all ${
                    !item.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'
                  }`}
                >
                  {item.image && (
                    <div className="aspect-video bg-zinc-800 relative">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      {!item.available && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                          <span className="px-4 py-2 bg-red-500 text-white font-bold rounded-lg transform -rotate-12">
                            SOLD OUT
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <h3 className="text-lg font-bold text-yellow-500">{item.name}</h3>
                      <span className="text-lg font-bold text-white">${item.price.toFixed(2)}</span>
                    </div>
                    {item.description && (
                      <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Cart Sidebar (desktop) */}
          {cart.length > 0 && (
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-24 bg-zinc-900 rounded-2xl p-4 border border-yellow-500/20">
                <h3 className="text-lg font-bold text-yellow-500 mb-4">Your Order</h3>

                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between items-start gap-2 bg-zinc-800 rounded-xl p-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
                            {item.quantity}x
                          </span>
                          <span className="text-white text-sm font-medium">{item.name}</span>
                        </div>
                        {item.modifiers.length > 0 && (
                          <p className="text-gray-500 text-xs mt-1">
                            {item.modifiers.map(m => m.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-500 font-bold">${item.totalPrice.toFixed(2)}</span>
                        <button
                          onClick={() => handleRemoveFromCart(item.id)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-yellow-500/30 pt-4">
                  <div className="flex justify-between text-lg font-bold mb-4">
                    <span className="text-white">Total</span>
                    <span className="text-yellow-500">${cartTotal.toFixed(2)}</span>
                  </div>

                  <button
                    onClick={handleStartCheckout}
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Cart Bar */}
        {cart.length > 0 && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-yellow-500/30 p-4">
            <button
              onClick={handleStartCheckout}
              className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors flex justify-between items-center px-6"
            >
              <span>View Cart ({cart.length} items)</span>
              <span>${cartTotal.toFixed(2)}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Item Info Modal Component
function ItemInfoModal({ item, onClose, onContinue, hasModifiers }) {
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden border-2 border-yellow-500">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl z-10"
        >
          X
        </button>

        {item.image && (
          <div className="aspect-video bg-zinc-800">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="p-6">
          <h2 className="text-2xl font-bold text-yellow-500 mb-2">{item.name}</h2>
          <p className="text-xl font-bold text-white mb-4">${item.price.toFixed(2)}</p>
          {item.description && (
            <p className="text-gray-400 mb-6">{item.description}</p>
          )}

          <button
            onClick={onContinue}
            className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg rounded-xl transition-colors"
          >
            {hasModifiers ? 'Continue' : 'Add to Order'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Modifier Modal Component
function ModifierModal({ group, selectedModifiers, onSelect, onContinue, onClose, progress }) {
  const sortedOptions = [...(group.options || [])].sort((a, b) => {
    const aIsNo = a.name.toLowerCase().startsWith('no ')
    const bIsNo = b.name.toLowerCase().startsWith('no ')
    if (aIsNo && !bIsNo) return -1
    if (!aIsNo && bIsNo) return 1
    return 0
  })

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden border-2 border-yellow-500">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl z-10"
        >
          X
        </button>

        <div className="p-6">
          <h2 className="text-xl font-bold text-yellow-500 mb-2">{group.displayName || group.name}</h2>
          <p className="text-gray-400 mb-6">
            {group.multiSelect ? 'Select one or more' : 'Tap to select'} {progress && `- ${progress}`}
          </p>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {sortedOptions.map(option => {
              const isSelected = selectedModifiers.includes(option.id)
              return (
                <button
                  key={option.id}
                  onClick={() => onSelect(option.id)}
                  className={`w-full px-4 py-3 rounded-xl flex justify-between items-center transition-all ${
                    isSelected
                      ? 'bg-yellow-500 text-black'
                      : 'bg-zinc-800 text-white border border-yellow-500/30 hover:border-yellow-500'
                  }`}
                >
                  <span className="font-medium">{option.name}</span>
                  {option.price > 0 && (
                    <span className={isSelected ? 'text-black/70' : 'text-gray-400'}>
                      +${option.price.toFixed(2)}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {group.multiSelect && (
            <button
              onClick={onContinue}
              disabled={selectedModifiers.length < (group.min || 1)}
              className="w-full mt-6 py-4 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/50 text-black font-bold text-lg rounded-xl transition-colors"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Optional Modifiers Modal Component
function OptionalModifiersModal({
  item,
  groups,
  selectedModifiers,
  onModifierChange,
  specialInstructions,
  onSpecialInstructionsChange,
  quantity,
  onQuantityChange,
  totalPrice,
  onAddToCart,
  onClose,
}) {
  const removeOptions = []
  const addOptions = []

  groups.forEach(group => {
    if (!group || !group.options) return
    group.options.forEach(option => {
      const isNoOption = option.name.toLowerCase().startsWith('no ')
      if (isNoOption) {
        removeOptions.push({ ...option, groupId: group.id, multiSelect: group.multiSelect !== false })
      } else {
        addOptions.push({ ...option, groupId: group.id, multiSelect: group.multiSelect !== false })
      }
    })
  })

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden border-2 border-yellow-500 flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl z-10"
        >
          X
        </button>

        <div className="p-6 border-b border-yellow-500/30">
          <h2 className="text-xl font-bold text-yellow-500">{item.name}</h2>
          <p className="text-gray-400">Customize your order (optional)</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Remove Options */}
          {removeOptions.length > 0 && (
            <div className="bg-red-900/20 rounded-xl p-4 border border-red-500/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">-</span>
                <span className="text-red-400 font-bold">REMOVE</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {removeOptions.map(option => {
                  const isSelected = (selectedModifiers[option.groupId] || []).includes(option.id)
                  return (
                    <button
                      key={option.id}
                      onClick={() => onModifierChange(option.groupId, option.id, option.multiSelect)}
                      className={`px-3 py-2 rounded-lg text-sm transition-all ${
                        isSelected
                          ? 'bg-red-500 text-white'
                          : 'bg-red-900/30 text-red-300 border border-red-500/30 hover:border-red-500'
                      }`}
                    >
                      {option.name.replace(/^no /i, '')}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Add Options */}
          {addOptions.length > 0 && (
            <div className="bg-zinc-800 rounded-xl p-4 border border-yellow-500/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold">+</span>
                <span className="text-yellow-500 font-bold">ADD</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {addOptions.map(option => {
                  const isSelected = (selectedModifiers[option.groupId] || []).includes(option.id)
                  return (
                    <button
                      key={option.id}
                      onClick={() => onModifierChange(option.groupId, option.id, option.multiSelect)}
                      className={`px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-1 ${
                        isSelected
                          ? 'bg-yellow-500 text-black'
                          : 'bg-zinc-700 text-white border border-yellow-500/30 hover:border-yellow-500'
                      }`}
                    >
                      <span>{option.name}</span>
                      {option.price > 0 && <span>+${option.price.toFixed(2)}</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Special Instructions</label>
            <textarea
              value={specialInstructions}
              onChange={(e) => onSpecialInstructionsChange(e.target.value)}
              placeholder="Any allergies or special requests?"
              className="w-full px-4 py-3 bg-zinc-800 border border-yellow-500/30 rounded-xl text-white focus:outline-none focus:border-yellow-500 resize-none h-20"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-yellow-500/30 bg-zinc-800">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-zinc-900 rounded-xl p-2 border border-yellow-500/30">
              <button
                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center text-black font-bold text-xl"
              >
                -
              </button>
              <span className="text-white font-bold text-lg w-8 text-center">{quantity}</span>
              <button
                onClick={() => onQuantityChange(quantity + 1)}
                className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center text-black font-bold text-xl"
              >
                +
              </button>
            </div>

            <button
              onClick={onAddToCart}
              className="flex-1 py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
            >
              Add to Order - ${totalPrice.toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
